import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../services/api';

const CODE_TTL_SECONDS = 60;

export type VerificationStatus =
  | 'idle'
  | 'sending'
  | 'awaitingCode'
  | 'verifying'
  | 'verified';

export function usePhoneVerification() {
  const [status, setStatus] = useState<VerificationStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const timerRef = useRef<number | null>(null);

  const stopTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => () => stopTimer(), [stopTimer]);

  const startTimer = useCallback(() => {
    stopTimer();
    setSecondsLeft(CODE_TTL_SECONDS);
    timerRef.current = window.setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          stopTimer();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  }, [stopTimer]);

  const start = useCallback(
    async (phone: string, name: string) => {
      setError(null);
      setStatus('sending');
      try {
        await api.verifyPhoneStart(phone, name);
        setStatus('awaitingCode');
        startTimer();
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Falha ao enviar código.');
        setStatus('idle');
      }
    },
    [startTimer],
  );

  const confirm = useCallback(
    async (phone: string, code: string): Promise<boolean> => {
      setError(null);
      setStatus('verifying');
      try {
        const r = await api.verifyPhoneConfirm(phone, code);
        if (r.verified) {
          stopTimer();
          setStatus('verified');
          return true;
        }
        setError('Código inválido.');
        setStatus('awaitingCode');
        return false;
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Falha ao validar código.');
        setStatus('awaitingCode');
        return false;
      }
    },
    [stopTimer],
  );

  const reset = useCallback(() => {
    stopTimer();
    setStatus('idle');
    setError(null);
    setSecondsLeft(0);
  }, [stopTimer]);

  return { status, error, secondsLeft, start, confirm, reset };
}
